import React, { useCallback, useEffect, useRef, useState } from 'react';
import useOutsideClick from '../../hooks/useOutsideClick';
import TimeInput from '../TimeInput/TimeInput';
import Icon from '../Icon/Icon';
import { DebounceInput } from 'react-debounce-input';
import { CustomButton } from '../Button/Button';
import { useChaptersContext } from '../../context/ChaptersContext';
import styles from './ChaptersEditor.module.scss';
import cx from 'classnames';
import { ENTER_KEY_CODE, ESCAPE_KEY_CODE } from '../../constants/common';
import { useTranslation } from 'react-i18next';

const MIN_TITLE_LENGTH = 1;

const ChapterItem = ({ id, title, startTime, min, max }) => {
  const [editedChapterTitle, setEditedChapterName] = useState(null);
  const [saveTitle, setSaveTitle] = useState(false);
  const [editTitle, setEditTitle] = useState(false);

  const editedNameRef = useRef(null);

  const { t } = useTranslation();

  useOutsideClick(editedNameRef, () => {
    editTitle && setSaveTitle(true);
  });

  const { activeChapter, handleRemoveChapter, onUpdatedChapter, onChooseChapter } = useChaptersContext();

  const handleStartEditName = (title, id) => {
    if (editTitle && editedChapterTitle?.id !== id) {
      setSaveTitle(true);
    }
    setTimeout(() => {
      setEditTitle(true);
      setEditedChapterName({ title, id });
    }, 100);
  };

  const handleUpdateChapter = e => {
    setEditedChapterName(prevState => ({ ...prevState, title: e.target.value }));
  };

  const handleKeydown = useCallback(
    event => {
      switch (event.keyCode) {
        case ESCAPE_KEY_CODE:
          setEditTitle(false);
          break;
        case ENTER_KEY_CODE:
          setSaveTitle(true);
          break;
        default:
          break;
      }
    },
    [editedChapterTitle]
  );

  useEffect(() => {
    if (saveTitle) {
      const trimmedTitle = editedChapterTitle.title.trim();
      if (trimmedTitle.length >= MIN_TITLE_LENGTH) {
        onUpdatedChapter({ title: trimmedTitle }, editedChapterTitle.id);
        setEditTitle(false);
      }
      setSaveTitle(false);
    }
  }, [saveTitle]);

  useEffect(() => {
    if (editTitle) {
      window.addEventListener('keydown', handleKeydown);
    } else {
      window.removeEventListener('keydown', handleKeydown);
    }
  }, [editTitle]);

  return (
    <li className={cx(styles.chaptersItem, { [styles.active]: activeChapter === id })} onClick={() => onChooseChapter(id)}>
      <TimeInput
        value={startTime / 1000}
        onChange={value => onUpdatedChapter({ startTime: value * 1000 }, id)}
        inputClassName={styles.timestampInput}
        containerClassName={styles.timestampContainer}
        min={min}
        max={max}
      />
      {editTitle && activeChapter === id ? (
        <DebounceInput
          element="input"
          type="text"
          id={id}
          debounceTimeout={300}
          autoFocus
          className={cx(styles.editNameInput, styles.editedChapter)}
          inputRef={editedNameRef}
          value={editedChapterTitle?.title}
          onChange={handleUpdateChapter}
          maxLength={70}
        />
      ) : (
        <span onClick={() => handleStartEditName(title, id)} className={styles.chapterItemName}>
          {title}
        </span>
      )}
      <CustomButton variant="link" onClick={() => handleRemoveChapter(id)} className={styles.binButton} ariaLabel={t('accessibility.bin')}>
        <Icon src="bin" className={styles.binIcon} />
      </CustomButton>
    </li>
  );
};

export default ChapterItem;
